import { useEffect, useState, useMemo } from "react";
import { format } from "date-fns";
import { getModelEvalStats } from "../../utils/model-eval";
import VisualizationDashboard from "./ModelEvalComponents/VisualizationDashboard";
import Sidebar from "../Sidebar";

export default function ModelEval({ setAuth, setUserOrg }) {
  const [evalMetrics, setEvalMetrics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd')
  });
  const [activeModel, setActiveModel] = useState(null);
  const [error, setError] = useState(null);
  const [isSampleMode, setIsSampleMode] = useState(true);
  const [showOnlyValid, setShowOnlyValid] = useState(false);
  const [copiedJobId, setCopiedJobId] = useState(null);

  const isValidEntry = (entry) => {
    const { structured_score, point_coverage_score, note_length_score, faithfulness_score } = entry;

    // Check if all scores are 0 (this usually means missing data)
    const allZeros =
      (structured_score === 0 || structured_score === "0") &&
      (point_coverage_score === 0 || point_coverage_score === "0") &&
      (note_length_score === 0 || note_length_score === "0") &&
      (faithfulness_score === 0 || faithfulness_score === "0");

    // If all are zero, consider it invalid
    if (allZeros) {
      return false;
    }

    // Otherwise, verify the scores are in the valid range
    return (
      structured_score !== undefined && structured_score >= 0 && structured_score <= 1 &&
      point_coverage_score !== undefined && point_coverage_score >= 0 && point_coverage_score <= 1 &&
      note_length_score !== undefined && !isNaN(parseFloat(note_length_score)) &&
      faithfulness_score !== undefined && faithfulness_score >= 0 && faithfulness_score <= 1
    );
  };

  const filteredMetrics = useMemo(() => {
    if (!evalMetrics || !showOnlyValid) {
      return evalMetrics;
    }

    // Deep clone to avoid modifying the original data
    const filtered = JSON.parse(JSON.stringify(evalMetrics));

    // Filter each model's evaluations
    Object.keys(filtered.stats.models).forEach(modelName => {
      const model = filtered.stats.models[modelName];

      // Filter evaluations
      const validEvaluations = model.evaluations.filter(isValidEntry);

      // If no valid evaluations, remove the model entirely
      if (validEvaluations.length === 0) {
        delete filtered.stats.models[modelName];
        return;
      }

      // Update evaluations array
      model.evaluations = validEvaluations;

      // Recalculate model stats
      model.structured_score = validEvaluations.reduce((sum, e) => sum + (e.structured_score || 0), 0) / validEvaluations.length;
      model.point_coverage_score = validEvaluations.reduce((sum, e) => sum + (e.point_coverage_score || 0), 0) / validEvaluations.length;
      model.note_length_score = validEvaluations.reduce((sum, e) => sum + (e.note_length_score || 0), 0) / validEvaluations.length;
      model.faithfulness_score = validEvaluations.reduce((sum, e) => sum + (e.faithfulness_score || 0), 0) / validEvaluations.length;
      model.total_evaluations = validEvaluations.length;
    });

    // Recalculate overall stats
    const remainingModels = Object.values(filtered.stats.models);
    if (remainingModels.length > 0) {
      // Count total valid evaluations
      const totalValidEvaluations = remainingModels.reduce((sum, model) => sum + model.total_evaluations, 0);
      filtered.stats.overall.total_evaluations = totalValidEvaluations;

      // Recalculate overall averages
      filtered.stats.overall.structured_score = remainingModels.reduce((sum, m) => sum + (m.structured_score * m.total_evaluations), 0) / totalValidEvaluations;
      filtered.stats.overall.point_coverage_score = remainingModels.reduce((sum, m) => sum + (m.point_coverage_score * m.total_evaluations), 0) / totalValidEvaluations;
      filtered.stats.overall.note_length_score = remainingModels.reduce((sum, m) => sum + (m.note_length_score * m.total_evaluations), 0) / totalValidEvaluations;
      filtered.stats.overall.faithfulness_score = remainingModels.reduce((sum, m) => sum + (m.faithfulness_score * m.total_evaluations), 0) / totalValidEvaluations;
    } else {
      // No valid models
      filtered.stats.overall = {
        structured_score: 0,
        point_coverage_score: 0,
        note_length_score: 0,
        faithfulness_score: 0,
        total_evaluations: 0
      };
    }

    return filtered;
  }, [evalMetrics, showOnlyValid]);

  const toggleValidFilter = () => {
    setShowOnlyValid(!showOnlyValid);
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(`${dateString}T12:00:00`);

      // Verify it's a valid date before formatting
      if (isNaN(date.getTime())) {
        console.warn('Invalid date:', dateString);
        return dateString;
      }

      return format(date, 'MMM dd, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      // Fallback to returning the original string
      return dateString;
    }
  };

  useEffect(() => {
    fetchEvalMetrics("", "", isSampleMode);
  }, []);

  const fetchEvalMetrics = async (start = "", end = "") => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getModelEvalStats(start, end, isSampleMode);
      setEvalMetrics(data);
      setActiveModel(null); // Reset active model when new data is loaded
    } catch (error) {
      console.error("Error fetching evaluation metrics:", error);
      setError("Failed to load evaluation data. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await fetchEvalMetrics(dateRange.startDate, dateRange.endDate);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatScore = (score) => {
    if (score === 0 || score === "0") {
      return "N/A";
    }
    return score ? score.toFixed(2) : "N/A";
  };

  const toggleSampleMode = () => {
    setIsSampleMode(!isSampleMode);
  };

  const formatDoctorName = (name) => {
    if (!name || name === 'Unknown') return 'Unknown';

    // Add a space before each capital letter (except the first one)
    return name.replace(/([A-Z])/g, (match, letter, offset) => {
      return offset > 0 ? ` ${letter}` : letter;
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setCopiedJobId(text);
        // Clear the notification after 2 seconds
        setTimeout(() => setCopiedJobId(null), 2000);
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
      });
  };

  return (
    <div className="flex min-h-screen bg-white">
      <div className="h-[100dvh]">
        <div className="flex h-[100%] fixed top-0">
          <Sidebar setAuth={setAuth} setUserOrg={setUserOrg} />
        </div>
      </div>
      <div
        className="w-full px-4 py-6"
        style={{
          marginLeft: "var(--sidebar-width, 250px)",
          width: "calc(100% - var(--sidebar-width, 250px))",
        }}
      >
        <div className="w-full max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-ktx-navy">Model Evaluation Dashboard</h1>
        <form onSubmit={handleSubmit} className="flex gap-4 items-end">
          <div className="flex flex-col">
            <label className="text-sm text-ktx-navy mb-1">Start Date</label>
            <input
              type="date"
              name="startDate"
              value={dateRange.startDate}
              onChange={handleInputChange}
              className="rounded-md border-gray-200 p-2 shadow-sm"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-sm text-ktx-navy mb-1">End Date</label>
            <input
              type="date"
              name="endDate"
              value={dateRange.endDate}
              onChange={handleInputChange}
              className="rounded-md border-gray-200 p-2 shadow-sm"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-sm text-ktx-navy mb-1">Data Mode</label>
            <button
              type="button"
              onClick={toggleSampleMode}
              className={`px-4 py-2 rounded-md text-sm font-medium h-[2.5rem] transition-colors ${isSampleMode
                ? 'bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200'
                : 'bg-blue-100 text-blue-800 border border-blue-300 hover:bg-blue-200'
                }`}
            >
              {isSampleMode ? 'Sample Data' : 'Complete Data'}
            </button>
          </div>
          <div className="flex flex-col">
            <label className="text-sm text-ktx-navy mb-1">Data Quality</label>
            <button
              type="button"
              onClick={toggleValidFilter}
              className={`px-4 py-2 rounded-md text-sm font-medium h-[2.5rem] transition-colors ${showOnlyValid
                ? 'bg-green-100 text-green-800 border border-green-300 hover:bg-green-200'
                : 'bg-gray-100 text-gray-800 border border-gray-300 hover:bg-gray-200'
                }`}
            >
              {showOnlyValid ? 'Valid Data Only' : 'All Data'}
            </button>
          </div>
          <button
            type="submit"
            className="bg-ktx-purple text-white px-6 py-2 rounded-md hover:bg-ktx-navy h-[2.5rem] disabled:opacity-50"
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : "Update"}
          </button>
        </form>
          </div>

          {/* {showOnlyValid && filteredMetrics && (
        <div className="bg-green-50 border-l-4 border-green-400 text-green-700 p-3 mb-6 text-sm" role="alert">
          <p>
            <span className="font-medium">Quality Filter:</span> Showing only valid entries (scores between 0-1).
          </p>
        </div>
      )}

      {evalMetrics && evalMetrics.isSample && (
        <div className="bg-amber-50 border-l-4 border-amber-400 text-amber-700 p-3 mb-6 text-sm" role="alert">
          <p>
            <span className="font-medium">Sample Mode:</span> Showing a partial dataset for faster loading.
          </p>
        </div>
      )} */}

      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6" role="alert">
          <p>{error}</p>
        </div>
      )}

          {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="w-12 h-12 border-4 border-ktx-purple border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : filteredMetrics ? (
        <div className="space-y-8">
          {filteredMetrics.dateRange.start === filteredMetrics.dateRange.end ?
            <div className="text-sm text-gray-500">
              Showing data from today
            </div>
            :
            <div className="text-sm text-gray-500">
              Showing data from {formatDisplayDate(filteredMetrics.dateRange.start)} to {formatDisplayDate(filteredMetrics.dateRange.end)}
            </div>
          }

          {/* Overall Stats */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-ktx-navy">Overall Performance</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="bg-light-purple/20 rounded-lg p-4">
                <div className="text-sm font-medium text-gray-500">Structure</div>
                <div className="text-2xl font-bold text-ktx-purple">
                  {formatScore(filteredMetrics.stats.overall.structured_score)}
                </div>
              </div>
              <div className="bg-light-purple/20 rounded-lg p-4">
                <div className="text-sm font-medium text-gray-500">Coverage</div>
                <div className="text-2xl font-bold text-ktx-purple">
                  {formatScore(filteredMetrics.stats.overall.point_coverage_score)}
                </div>
              </div>
              <div className="bg-light-purple/20 rounded-lg p-4">
                <div className="text-sm font-medium text-gray-500">Length</div>
                <div className="text-2xl font-bold text-ktx-purple">
                  {formatScore(filteredMetrics.stats.overall.note_length_score)}
                </div>
              </div>
              <div className="bg-light-purple/20 rounded-lg p-4">
                <div className="text-sm font-medium text-gray-500">Faithfulness</div>
                <div className="text-2xl font-bold text-ktx-purple">
                  {formatScore(filteredMetrics.stats.overall.faithfulness_score)}
                </div>
              </div>
            </div>
            <div className="mt-4 text-sm text-gray-500">
              Total evaluations: {filteredMetrics.stats.overall.total_evaluations}
              {showOnlyValid && <span className="ml-2 text-green-600">(valid entries only)</span>}
            </div>
          </div>

          {/* Models Comparison */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-ktx-navy">Model Performance Comparison</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-dark-purple text-white">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Model</th>
                    <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider">Structure</th>
                    <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider">Coverage</th>
                    <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider">Length</th>
                    <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider">Faithfulness</th>
                    <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider">Evaluations</th>
                    <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Object.entries(filteredMetrics.stats.models).map(([modelName, modelStats], index) => (
                    <tr
                      key={modelName}
                      className={index % 2 === 0 ? 'bg-light-purple/10' : 'bg-white'}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-ktx-navy">{modelName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center">{formatScore(modelStats.structured_score)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center">{formatScore(modelStats.point_coverage_score)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center">{formatScore(modelStats.note_length_score)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center">{formatScore(modelStats.faithfulness_score)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center">{modelStats.total_evaluations}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                        <button
                          onClick={() => setActiveModel(activeModel === modelName ? null : modelName)}
                          className="text-ktx-purple hover:text-ktx-navy"
                        >
                          {activeModel === modelName ? 'Hide Details' : 'Show Details'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Detail View for Selected Model */}
          {activeModel && filteredMetrics.stats.models[activeModel] && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4 text-ktx-navy">
                Detail View: {activeModel}
              </h2>

              {/* Create a container with fixed height for scrolling */}
              <div className="border border-gray-200 rounded-md">
                {/* The table header remains outside the scrollable area */}
                <div className="bg-dark-purple text-white">
                  <table className="min-w-full table-fixed">
                    <thead>
                      <tr>
                        <th className="w-[10%] px-6 py-3 text-center text-xs font-medium uppercase tracking-wider">#</th>
                        <th className="w-[20%] px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Doctor</th>
                        <th className="w-[17.5%] px-6 py-3 text-center text-xs font-medium uppercase tracking-wider">Structure</th>
                        <th className="w-[17.5%] px-6 py-3 text-center text-xs font-medium uppercase tracking-wider">Coverage</th>
                        <th className="w-[17.5%] px-6 py-3 text-center text-xs font-medium uppercase tracking-wider">Length</th>
                        <th className="w-[17.5%] px-6 py-3 text-center text-xs font-medium uppercase tracking-wider">Faithfulness</th>
                      </tr>
                    </thead>
                  </table>
                </div>

                {/* Scrollable container for the table body */}
                <div className="max-h-96 overflow-y-auto">
                  <table className="min-w-full table-fixed">
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredMetrics.stats.models[activeModel].evaluations.map((val, index) => (
                        <tr
                          key={index}
                          className={`${index % 2 === 0 ? 'bg-light-purple/10' : 'bg-white'} hover:bg-gray-100 cursor-pointer p-1`}
                          onClick={() => copyToClipboard(val.job_name)}
                          title={`Click to copy job ID: ${val.job_name}`}
                        >
                          <td className="w-[10%] px-6 py-3 whitespace-nowrap text-sm text-center">{index + 1}</td>
                          <td className="w-[20%] px-6 py-3 whitespace-nowrap text-sm text-left">{formatDoctorName(val.doctor)}</td>
                          <td className="w-[17.5%] px-6 py-3 whitespace-nowrap text-sm text-center">{formatScore(val.structured_score)}</td>
                          <td className="w-[17.5%] px-6 py-3 whitespace-nowrap text-sm text-center">{formatScore(val.point_coverage_score)}</td>
                          <td className="w-[17.5%] px-6 py-3 whitespace-nowrap text-sm text-center">{formatScore(val.note_length_score)}</td>
                          <td className="w-[17.5%] px-6 py-3 whitespace-nowrap text-sm text-center">{formatScore(val.faithfulness_score)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-4 text-sm text-gray-500 flex justify-between items-center">
                <span>Total evaluations: {filteredMetrics.stats.models[activeModel].evaluations.length}</span>
                <div>
                  {showOnlyValid && <span className="text-green-600">Showing valid entries only</span>}
                  <span className="ml-4 text-gray-500 italic">Click any row to copy its job ID</span>
                </div>
              </div>

              {/* Toast notification for copied job ID */}
              {copiedJobId && (
                <div className="fixed bottom-4 right-4 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded shadow-lg z-50">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    <p>Job ID copied to clipboard!</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
          ) : (
        <div className="text-center text-gray-500 py-12">No evaluation data available</div>
      )}

      {filteredMetrics && (
        <VisualizationDashboard data={filteredMetrics} />
          )}
        </div>
      </div>
    </div>
  );
}
